/**
 * Nombres en MOTS arabes — connaissance linguistique, pas logique d'application.
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * ─── POURQUOI UN FICHIER À PART ──────────────────────────────────────────────
 *
 * Ce code ne sait rien de TicketFlow : ni ticket, ni guichet, ni file. Il convertit un
 * entier en mots arabes, et c'est tout. Le laisser dans un hook React d'annonce vocale
 * l'aurait rendu invisible à qui en a besoin ailleurs — or tout produit de l'écosystème qui
 * ANNONCE un nombre en arabe rencontrera le même moteur et le même défaut (MostaGare a des
 * quais et des numéros d'ordre, la même chaîne de synthèse).
 *
 * C'est donc un candidat module `@mostajs` déclaré (docs/IDEES-MODULES-TRANSVERSES.md, §4).
 * Il reste ICI pour l'instant, et délibérément : les formes doivent d'abord être validées par
 * un locuteur. Publier un paquet dont l'accord arabe est faux propagerait l'erreur à tout
 * l'écosystème — un module est exactement l'endroit où une erreur devient chère à corriger.
 *
 * Ce fichier est la COUTURE : l'extraction se réduira à le déplacer et à changer un import.
 * Aucun appelant n'a à connaître son emplacement futur.
 */

/**
 * Nombre → MOTS arabes. Employé pour les seules annonces ARABES.
 *
 * Constaté sur le site Yalidine (2026-09-13), voix arabe du poste d'affichage : le ticket
 * `تحاليل الرجال 10` est prononcé « ZÉRO UN ». Pas « un zéro » — « zéro un » : le moteur
 * restitue les chiffres dans l'ordre VISUEL du texte arabe, de droite à gauche. Il ne voit
 * donc pas un nombre, mais deux caractères isolés qu'il énumère.
 *
 * Les chiffres arabo-indiens (`١٠`) subissent le même sort — vérifié sur place avec
 * `tools/test-voix-arabe.html`. Aucune ÉCRITURE de chiffre ne sera normalisée par ce moteur.
 *
 * D'où ce dernier recours : ne plus lui donner de chiffre du tout. « عشرة » n'a rien à
 * normaliser, ce ne sont que des mots — et aucun moteur ne peut les inverser.
 *
 * La règle « la voix dit ce que l'écran affiche » tient : un nombre écrit en toutes lettres
 * reste le MÊME nombre. L'écran, le papier et les annonces FR/EN ne changent pas.
 *
 * RÉSERVE ASSUMÉE : formes masculines cardinales, usage courant pour annoncer un numéro.
 * L'accord arabe (genre, duel, cas) est plus subtil. À faire valider par un locuteur — la
 * question est linguistique, elle ne se tranche pas dans le code.
 */
const AR_UNITES = ['صفر', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة',
  'عشرة', 'أحد عشر', 'اثنا عشر', 'ثلاثة عشر', 'أربعة عشر', 'خمسة عشر',
  'ستة عشر', 'سبعة عشر', 'ثمانية عشر', 'تسعة عشر'];
const AR_DIZAINES = ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];
const AR_CENTAINES = ['', 'مائة', 'مائتان', 'ثلاثمائة', 'أربعمائة', 'خمسمائة', 'ستمائة', 'سبعمائة', 'ثمانمائة', 'تسعمائة'];

export function motsAr(valeur) {
  // Le test porte sur la CHAÎNE, pas sur `Number(valeur)` : `Number('')` vaut 0, et une valeur
  // vide serait alors annoncée « صفر » — un zéro inventé de toutes pièces. Seule une suite de
  // chiffres est convertie ; tout le reste est rendu tel quel.
  const brut = String(valeur ?? '').trim();
  if (!/^\d+$/.test(brut)) return String(valeur ?? '');
  const n = Number(brut);
  // Au-delà de 999, on rend le chiffre plutôt qu'une traduction inventée. Un numéro de ticket
  // n'y monte pas ; si cela arrivait, mieux vaut un nombre mal lu qu'un mot faux.
  if (n > 999) return brut;
  if (n < 20) return AR_UNITES[n];
  if (n < 100) {
    const d = Math.floor(n / 10);
    const u = n % 10;
    return u ? `${AR_UNITES[u]} و${AR_DIZAINES[d]}` : AR_DIZAINES[d];
  }
  const c = Math.floor(n / 100);
  const r = n % 100;
  return AR_CENTAINES[c] + (r ? ` و${motsAr(r)}` : '');
}

/**
 * Chaque suite de chiffres d'une phrase arabe, écrite en toutes lettres.
 *
 * Appliqué à la phrase ENTIÈRE et non au seul libellé : le numéro de guichet est un nombre
 * lui aussi, et il se ferait inverser exactement de la même façon.
 */
export const nombresEnMotsAr = (phrase) => String(phrase ?? '').replace(/\d+/g, (n) => motsAr(n));
